import { InspectionConclusionEnum } from "commons/enum/inspection";
import { Instance, SnapshotIn, types } from "mobx-state-tree";
import { Base } from "../base";
import { Customer } from "../customers/customer";
import { Order } from "../orders/order";
import { Product } from "../products/product";
import { WorkOrder } from "../work-order/work-order";
import { WorkOrderPo } from "../work-order-po/work-order-po";

export const InspectionNoteItem = types.compose(
  Base,
  types.model({
    productId: types.maybeNull(types.string),
    product: types.maybeNull(Product),
    appearance: types.maybeNull(types.string),
    amount: types.maybeNull(types.string),
    appearanceConclusion: types.maybeNull(
      types.enumeration<InspectionConclusionEnum>(
        "appearanceConclusion",
        Object.values(InspectionConclusionEnum)
      )
    ), //Enum: Passed or Failed
    widthConclusion: types.maybeNull(
      types.enumeration<InspectionConclusionEnum>(
        "widthConclusion",
        Object.values(InspectionConclusionEnum)
      )
    ), //Enum: Passed or Failed
    lengthConclusion: types.maybeNull(
      types.enumeration<InspectionConclusionEnum>(
        "lengthConclusion",
        Object.values(InspectionConclusionEnum)
      )
    ), //Enum: Passed or Failed
    heightConclusion: types.maybeNull(
      types.enumeration<InspectionConclusionEnum>(
        "heightConclusion",
        Object.values(InspectionConclusionEnum)
      )
    ), //Enum: Passed or Failed
    quantificationResult: types.maybeNull(types.number),
    quantificationConclusion: types.maybeNull(
      types.enumeration<InspectionConclusionEnum>(
        "quantificationConclusion",
        Object.values(InspectionConclusionEnum)
      )
    ), //Enum: Passed or Failed
    foilResult: types.maybeNull(types.number),
    foilConclusion: types.maybeNull(
      types.enumeration<InspectionConclusionEnum>(
        "foilConclusion",
        Object.values(InspectionConclusionEnum)
      )
    ), //Enum: Passed or Failed
    printPaperSPUnit: types.maybeNull(types.string),
    appearanceInspectionStandard: types.maybeNull(types.string),
    inspector: types.maybeNull(types.string),
    // Materials
    paperTypeInspect: types.maybeNull(types.number),
    paperTypeResult: types.maybeNull(
      types.enumeration<InspectionConclusionEnum>(
        "paperTypeResult",
        Object.values(InspectionConclusionEnum)
      )
    ),

    thicknessInspect: types.maybeNull(types.number),
    thicknessResult: types.maybeNull(
      types.enumeration<InspectionConclusionEnum>(
        "thicknessResult",
        Object.values(InspectionConclusionEnum)
      )
    ),

    weightInspect: types.maybeNull(types.number),
    weightResult: types.maybeNull(
      types.enumeration<InspectionConclusionEnum>(
        "weightResult",
        Object.values(InspectionConclusionEnum)
      )
    ),

    // Dimensions
    lengthInspect: types.maybeNull(types.number),
    lengthResult: types.maybeNull(
      types.union(
        types.number,
        types.enumeration<InspectionConclusionEnum>(
          "lengthResult",
          Object.values(InspectionConclusionEnum)
        )
      )
    ),

    widthInspect: types.maybeNull(types.number),
    widthResult: types.maybeNull(
      types.union(
        types.maybeNull(types.number),
        types.enumeration<InspectionConclusionEnum>(
          "widthResult",
          Object.values(InspectionConclusionEnum)
        )
      )
    ),

    heightInspect: types.maybeNull(types.number),
    heightResult: types.maybeNull(
      types.union(
        types.maybeNull(types.number),
        types.enumeration<InspectionConclusionEnum>(
          "heightResult",
          Object.values(InspectionConclusionEnum)
        )
      )
    ),

    // Standards
    approvalInspect: types.maybeNull(types.string),
    approvalResult: types.maybeNull(
      types.enumeration<InspectionConclusionEnum>(
        "approvalResult",
        Object.values(InspectionConclusionEnum)
      )
    ),

    printColorInspect: types.maybeNull(types.string),
    printColorResult: types.maybeNull(
      types.enumeration<InspectionConclusionEnum>(
        "printColorResult",
        Object.values(InspectionConclusionEnum)
      )
    ),

    appearanceInspect: types.maybeNull(types.string),
    appearanceResult: types.maybeNull(
      types.union(
        types.maybeNull(types.string),
        types.enumeration<InspectionConclusionEnum>(
          "appearanceResult",
          Object.values(InspectionConclusionEnum)
        )
      )
    ),
    otherStandardInspectionStandard: types.maybeNull(types.string),
    otherStandardInspect: types.maybeNull(types.string),
    otherStandardResult: types.maybeNull(
      types.enumeration<InspectionConclusionEnum>(
        "otherStandardResult",
        Object.values(InspectionConclusionEnum)
      )
    ),

    // Final Conclusion
    conclusion: types.maybeNull(types.string),
  })
);

export const InspectionNote = types
  .compose(
    Base,
    types.model({
      orderNumber: types.maybeNull(types.number),
      number: types.maybeNull(types.string),
      workOrderId: types.maybeNull(types.string),
      workOrder: types.maybeNull(WorkOrder),
      workOrderPOId: types.maybeNull(types.string),
      workOrderPO: types.maybeNull(WorkOrderPo),
      customerId: types.maybeNull(types.string),
      customer: types.maybeNull(Customer),
      inspectionDate: types.maybeNull(types.string),
      manufacturingDate: types.maybeNull(types.string),
      expireDate: types.maybeNull(types.string),
      conclusion: types.maybeNull(types.string),
      purchaseOrderId: types.maybeNull(types.string),
      purchaseOrder: types.maybeNull(Order),
      deliveredQuantity: types.maybeNull(types.number),
      packagesQuantity: types.maybeNull(types.string),
      quantityPerPackage: types.maybeNull(types.number),
      inspectionSamples: types.maybeNull(types.string),
      storageTemperature: types.maybeNull(types.string),
      note: types.maybeNull(types.string),
      inspector: types.maybeNull(types.string),
      expireDateText: types.maybeNull(types.string),
      items: types.optional(types.array(InspectionNoteItem), []),
      type: types.maybeNull(types.number), // "DualLang" or "Stella"
    })
  )
  .views((self) => ({
    get orderQuantity() {
      const inspectProductId = self.items?.[0]?.productId;
      if (!inspectProductId) return 0;

      // Prioritize workOrderPO
      const poItem = self.workOrderPO?.purchaseOrderItem;
      if (poItem?.productId === inspectProductId) {
        return poItem.quantityStandard ?? 0;
      }

      // Fall back to workOrder
      const woItem = self.workOrder?.items?.find(
        (x) => x.productId === inspectProductId
      );
      return woItem?.amount ?? 0;
    },
    get product() {
      return self.items?.[0]?.product;
    },
  }));

export interface IInspectionNote extends Instance<typeof InspectionNote> {}
export interface IInspectionNoteProperties
  extends SnapshotIn<typeof InspectionNote> {}
